import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Plus, Trash2, Users } from 'lucide-react';

export interface Reference {
    id: string;
    name: string;
    position: string;
    company: string;
    email: string;
    phone: string;
}

interface ReferencesFormProps {
    data: Reference[];
    onChange: (data: Reference[]) => void;
}

const ReferencesForm: React.FC<ReferencesFormProps> = ({ data, onChange }) => {
    const addReference = () => {
        onChange([
            ...data,
            {
                id: Date.now().toString(),
                name: '',
                position: '',
                company: '',
                email: '',
                phone: ''
            }
        ]);
    };

    const removeReference = (id: string) => {
        onChange(data.filter(item => item.id !== id));
    };

    const updateReference = (id: string, field: keyof Reference, value: string) => {
        onChange(data.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    return (
        <Card className="w-full">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    References
                </CardTitle>
                <Button onClick={addReference} size="sm" className="flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Add Reference
                </Button>
            </CardHeader>
            <CardContent className="space-y-6">
                {data.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                        <p>No references added yet.</p>
                        <Button variant="link" onClick={addReference}>Add your first reference</Button>
                    </div>
                ) : (
                    data.map((item, index) => (
                        <div key={item.id} className="p-4 border rounded-lg space-y-4 relative bg-gray-50/50">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => removeReference(item.id)}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Full Name</Label>
                                    <Input
                                        value={item.name}
                                        onChange={(e) => updateReference(item.id, 'name', e.target.value)}
                                        placeholder=""
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Job Title / Position</Label>
                                    <Input
                                        value={item.position}
                                        onChange={(e) => updateReference(item.id, 'position', e.target.value)}
                                        placeholder=""
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Company / Organization</Label>
                                    <Input
                                        value={item.company}
                                        onChange={(e) => updateReference(item.id, 'company', e.target.value)}
                                        placeholder=""
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email Address</Label>
                                    <Input
                                        value={item.email}
                                        onChange={(e) => updateReference(item.id, 'email', e.target.value)}
                                        placeholder=""
                                        type="email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Phone Number</Label>
                                    <Input
                                        value={item.phone}
                                        onChange={(e) => updateReference(item.id, 'phone', e.target.value)}
                                        placeholder=""
                                        type="tel"
                                    />
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </CardContent>
        </Card>
    );
};

export default ReferencesForm;
